import { Injectable } from '@angular/core';

import { BehaviorSubject, combineLatest, map, Observable } from 'rxjs';

import { SortOrderEnum } from '../../../api/shared/enums/sort-order.enum';
import { LocalizedEnum } from '../../../api/journodex/model/localized-enum';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { SortOrderServiceInterface } from '../../../Common/Misc/services/sort-order.service.interface';

import { ChartSetupOptionModel } from '../models/chart-setup-option.model';
import { ChartDataTypesEnum, ChartDataTypesToPermissionsMap } from '../enums/chart-data-types.enum';
import { ChartTypesEnum } from '../enums/chart-types.enum';
import { ChartSourceTypesEnum } from '../enums/chart-source-types.enum';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { FilterService } from '../../../Common/Filter/services/abstract-filter.service';
import { FilterTypeModel } from '../../../Common/Filter/models/abstract-filter-type.model';
import { FilterTypeCheckboxModel } from '../../../Common/Filter/models/filter-type-checkbox.model';
import { ChartSetupFiltersEnum } from '../enums/chart-setup-filters.enum';
import { FilterOptionModel } from '../../../Common/Filter/models/filter-option.model';

export interface ChartGroupModel {
    name: string;
    children: ChartSetupOptionModel[];
    dataType?: ChartDataTypesEnum;
}

interface ChartFilterModel {
    value: any;
    label: string;
}
@Injectable()
export class ChartSetupSearchService extends FilterService implements SortOrderServiceInterface {
    protected sortOrder: SortOrderEnum = SortOrderEnum.ascending;
    protected sortOrder$: BehaviorSubject<SortOrderEnum> = new BehaviorSubject<SortOrderEnum>(this.sortOrder);
    protected sortField: LocalizedEnum = null;
    protected sortField$: BehaviorSubject<LocalizedEnum> = new BehaviorSubject<LocalizedEnum>(this.sortField);
    protected allFilters: FilterTypeModel[];
    protected dataTypesFilter: FilterTypeCheckboxModel<string> = new FilterTypeCheckboxModel<string>(
        ChartSetupFiltersEnum.dataTypes,
        'ChartSetupDialog.dataTypes',
    );
    protected chartTypesFilter: FilterTypeCheckboxModel<string> = new FilterTypeCheckboxModel<string>(
        ChartSetupFiltersEnum.chartTypes,
        'ChartSetupDialog.chartTypes'
    );
    protected dataSourcesFilter: FilterTypeCheckboxModel<string> = new FilterTypeCheckboxModel<string>(
        ChartSetupFiltersEnum.dataSources,
        'Reports.formField.dataSource.singleSeries.label'
    );
    chartSortFields$: BehaviorSubject<LocalizedEnum[]> = new BehaviorSubject<LocalizedEnum[]>([]);
    sortFields: LocalizedEnum[] = [
        {
            enumValue: 'ALL',
            displayString: 'ChartSetupDialog.allCharts'
        },
        {
            enumValue: 'CHART_TYPES',
            displayString: 'ChartSetupDialog.chartTypes'
        },
        {
            enumValue: 'DATA_TYPES',
            displayString: 'ChartSetupDialog.dataTypes'
        },
        {
            enumValue: 'SOURCE_TYPES',
            displayString: 'ChartSetupDialog.sourceTypes'
        }
    ];

    selectedDataTypeFilters: ChartFilterModel[] = [];
    selectedChartTypeFilters: ChartFilterModel[] = [];
    selectedDataSourceFilters: ChartFilterModel[] = [];
    defaultChartOptions: ChartSetupOptionModel[] = [];
    results: ChartSetupOptionModel[] = [];
    private results$: BehaviorSubject<ChartSetupOptionModel[]> = new BehaviorSubject<ChartSetupOptionModel[]>(this.results);

    constructor(
        private localizationService: LocalizationService,
        private currentUserStateService: CurrentUserStateService
    ) {
        super();
        // Sort Field
        this.chartSortFields$.next(this.sortFields);
        this.sortField = this.sortFields.find(field => field.enumValue === 'DATA_TYPES');
        this.sortField$.next(this.sortField);

        this.allFilters = this.getFilterDefinitions();
        // Set filter options
        this.dataTypesFilter.options = this.createFilterOptions(ChartDataTypesEnum);
        this.chartTypesFilter.options = this.createFilterOptions(ChartTypesEnum);
        this.dataSourcesFilter.options = this.createFilterOptions(ChartSourceTypesEnum);
        // show filter label
        this.dataTypesFilter.showFilterLabel = true;
        this.chartTypesFilter.showFilterLabel = true;
        this.dataSourcesFilter.showFilterLabel = true;

        this.setFilters(this.allFilters);
        this.loadResults();
    }

    setDefaultChartOptions(chartOptions: ChartSetupOptionModel[]) {
        this.defaultChartOptions = chartOptions;
    }
    setResults(chartOptions: ChartSetupOptionModel[]) {
        this.results = chartOptions;
    }
    getSortableFields(): Observable<any[]> {
        return this.chartSortFields$;
    }
    getSortOrder(): Observable<SortOrderEnum> {
        return this.sortOrder$;
    }
    getSortField(): Observable<any> {
        return this.sortField$;
    }
    setSortOrder(order: SortOrderEnum): void {
        if (this.sortOrder !== order) {
            this.sortOrder = order;
            this.sortOrder$.next(order);
            this.loadResults();
        };
    }
    setSortField(field: any): void {
        if (this.sortField !== field) {
            this.sortField = field;
            this.sortField$.next(field);
            this.loadResults();
        };
    }

    applyFilters() {
        super.applyFilters();
        this.loadResults();
    }

    loadResults(): void {
        this.results = this.defaultChartOptions;
        this.handleSort();
        this.handleFilter();
        this.results$.next(this.results);
    }

    getResults(): Observable<ChartSetupOptionModel[]> {
        return this.results$;
    }

    override getFilters(): Observable<FilterTypeModel<any>[]> {
        return combineLatest([
            this.currentUserStateService.getPermissions(),
            this.filters$
        ]).pipe(
            // filter out dataTypes without correct user permissions
            map(([permissions, filterGroups]) => {
                const dataTypeFilters = filterGroups.find(group => group.id === ChartSetupFiltersEnum.dataTypes) as FilterTypeCheckboxModel;
                if (!dataTypeFilters) {
                    return filterGroups;
                }

                dataTypeFilters.options = dataTypeFilters.options.filter(opt => {
                    const value = Number(opt.value) as ChartDataTypesEnum;
                    const dataTypePermissions = ChartDataTypesToPermissionsMap.get(value) ?? [];
                    return permissions.some(permission => dataTypePermissions.includes(permission));
                });
                return filterGroups;
            })
        );
    }

    private handleSort() {
        if (this.sortOrder === SortOrderEnum.ascending) {
            this.results.sort(
                (a, b) => {
                    const labelOfA = this.localizationService.transform(a.blockType.label).toLocaleLowerCase();
                    const labelOfB = this.localizationService.transform(b.blockType.label).toLocaleLowerCase();
                    return labelOfA.localeCompare(labelOfB);
                }
            );
        } else {
            this.results.sort(
                (a, b) => {
                    const labelOfA = this.localizationService.transform(a.blockType.label).toLocaleLowerCase();
                    const labelOfB = this.localizationService.transform(b.blockType.label).toLocaleLowerCase();
                    return labelOfB.localeCompare(labelOfA);
                }
            );
        }
    }

    private handleFilter() {
        this.selectedDataTypeFilters = this.setSelectedFilters(this.dataTypesFilter);
        this.selectedDataSourceFilters = this.setSelectedFilters(this.dataSourcesFilter);
        this.selectedChartTypeFilters = this.setSelectedFilters(this.chartTypesFilter);
        if (this.selectedDataTypeFilters.length > 0) {
            this.results = this.results.filter(
                chart => {
                    return !!this.selectedDataTypeFilters.find(filter => chart.dataTypes.includes(filter.value));
                }
            );
        }
        if (this.selectedChartTypeFilters.length > 0) {
            this.results = this.results.filter(chart => {
                return !!this.selectedChartTypeFilters.find(filter => chart.chartTypes.find(ct => ct.key === filter.value));
            });
        }
        if (this.selectedDataSourceFilters.length > 0) {
            this.results = this.results.filter(chart => {
                return !!this.selectedDataSourceFilters.find(filter => chart.chartSourceType === filter.value);
            });
        }
    }

    private createFilterOptions(enumuration: any): FilterOptionModel[] {
        const filterOptions: FilterOptionModel[] = [];
        Object.entries(enumuration)
            .filter(([k, v]) => isNaN(Number(k)))
            /**
             * TODO: Remove "Recent Activity" filter for now. We will decide whether to remove the Recent Activity charts
             * from Chart Setup after the new Recent Activity widget is released on the landing page.
             */
            .filter(([k, v]) => k !== 'recentActivity')
            .forEach(([k, v]) => {
                // Key  is the string value of the object key, value is the actual value of the enumeration
                filterOptions.push({
                    value: Number(v),
                    label: this.localizationService.transform(`ChartSetupDialog.filter.${k}.${v}`)
                });
            });
        return filterOptions;
    }


    getFilterDefinitions(): FilterTypeModel[] {
        return [
            this.dataSourcesFilter,
            this.dataTypesFilter,
            this.chartTypesFilter
        ];
    }

    setSelectedFilters(filter: FilterTypeCheckboxModel): ChartFilterModel[] {
        const selectedFilters: ChartFilterModel[] = [];
        filter.getValue().forEach(
            value => {
                selectedFilters.push(filter.options.find(option => option.value === value));
            }
        );
        return selectedFilters;
    }
}
